import type {
  DisciplineType,
  GroupDetail,
  GroupEntry,
  GroupMember,
} from '../../services/api/types.gen';

export type EntryRef =
  | { kind: 'user' | 'role'; id: string }
  | {
      kind: 'source';
      clinic_id?: string | null;
      discipline?: DisciplineType | null;
    };

/** A source has no id of its own on the wire: its clinic and discipline are
 *  its identity. */
export function sameEntry(a: EntryRef, b: EntryRef): boolean {
  if (a.kind === 'source' || b.kind === 'source') {
    return (
      a.kind === 'source' &&
      b.kind === 'source' &&
      (a.clinic_id ?? null) === (b.clinic_id ?? null) &&
      (a.discipline ?? null) === (b.discipline ?? null)
    );
  }
  return a.kind === b.kind && a.id === b.id;
}

export function toEntryRef(entry: GroupEntry): EntryRef {
  return entry.kind === 'source'
    ? {
        kind: 'source',
        clinic_id: entry.clinic_id ?? null,
        discipline: entry.discipline ?? null,
      }
    : { kind: entry.kind, id: entry.id };
}

export function entriesFromDetail(detail: GroupDetail): EntryRef[] {
  return detail.entries.map(toEntryRef);
}

export function addEntry(entries: EntryRef[], entry: EntryRef): EntryRef[] {
  return entries.some(e => sameEntry(e, entry)) ? entries : [...entries, entry];
}

export function removeEntry(entries: EntryRef[], entry: EntryRef): EntryRef[] {
  return entries.filter(e => !sameEntry(e, entry));
}

export type RosterSection = {
  /** The role or source behind the section; null for "Added individually". */
  entry: GroupEntry | null;
  people: GroupMember[];
};

/** One section per role, then one per source, then "Added individually" when
 *  it has rows. A person who is both a holder and a manual row appears once,
 *  under the role or source — the sync would put a removed holder back, so
 *  their row carries no remove control. */
export function buildRoster(detail: GroupDetail): RosterSection[] {
  const sections: RosterSection[] = [
    ...detail.entries
      .filter(e => e.kind === 'role')
      .map(entry => ({
        entry,
        people: detail.members.filter(m => m.via_roles.includes(entry.id)),
      })),
    ...detail.entries
      .filter(e => e.kind === 'source')
      .map(entry => ({
        entry,
        people: detail.members.filter(m => m.via_sources.includes(entry.id)),
      })),
  ];
  const manualOnly = detail.members.filter(
    m =>
      m.added_individually &&
      m.via_roles.length === 0 &&
      m.via_sources.length === 0
  );
  return manualOnly.length > 0
    ? [...sections, { entry: null, people: manualOnly }]
    : sections;
}

/** Members of a role or source section whose only reason to be in the group is
 *  that entry: the people who leave when it is removed. */
export function leavingMembers(
  detail: GroupDetail,
  entry: GroupEntry
): GroupMember[] {
  return detail.members.filter(m => {
    const roles = m.via_roles.filter(
      id => entry.kind !== 'role' || id !== entry.id
    );
    const sources = m.via_sources.filter(
      id => entry.kind !== 'source' || id !== entry.id
    );
    const inEntry =
      entry.kind === 'role'
        ? m.via_roles.includes(entry.id)
        : m.via_sources.includes(entry.id);
    return (
      inEntry &&
      roles.length === 0 &&
      sources.length === 0 &&
      !m.added_individually
    );
  });
}

/** The confirmation text for removing a role or source. */
export function removeEntryMessage(
  detail: GroupDetail,
  entry: GroupEntry
): string {
  const count = leavingMembers(detail, entry).length;
  if (count === 0) {
    return 'No one leaves the group. Everyone here also belongs through another role, source or individual add.';
  }
  const noun =
    entry.kind === 'source'
      ? count === 1
        ? 'person'
        : 'people'
      : count === 1
        ? 'holder'
        : 'holders';
  return `Its ${count} ${noun} ${count === 1 ? 'leaves' : 'leave'} the group and ${count === 1 ? 'stops' : 'stop'} seeing new messages. People added individually stay.`;
}

export function peopleCount(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'}`;
}

export function rosterHeadline(detail: GroupDetail): string {
  return `${peopleCount(detail.members.length)} in the conversation`;
}

/** The channel to open from a drawer, or null. Chat shows a channel to its
 *  members only, so a non-member administrator gets no conversation to open. */
export function conversationChannelId(
  detail: GroupDetail,
  userId: string | undefined
): string | null {
  if (!detail.channel_id || !userId) return null;
  return detail.members.some(member => member.user_id === userId)
    ? detail.channel_id
    : null;
}
